import { createContext, useContext, useMemo } from 'react';
import { useMutation } from 'react-query';
import AuthService from '@services/AuthService';
import { useNotificationsSSE } from '@hooks/useNotificationsSSE';
import CurrentUserContext from '@contexts/CurrentUserContext';
import NotificationsService from '@services/NotificationsService';
import { NOTIFICACIONES } from '@notificaciones/utils/constants';
import { Features } from './Features/Features';

const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
  const { userData } = useContext(CurrentUserContext);

  const typeToken = AuthService.getTypeToken();
  const accessToken = AuthService.getAccessToken();

  const {
    notificaciones,
    error,
    isConnected,
    optimisticUpdateLeido,
    hasResolvedInitialNotifications,
  } =
    useNotificationsSSE({
      typeToken,
      accessToken,
      enabled: !!userData && Features.NOTIFICACIONES,
    });

  const { mutate: marcarComoLeida } = useMutation(
    NotificationsService.notificacionLeida,
    {
      onMutate: async (idNotificacion) => {
        const previous = notificaciones;

        optimisticUpdateLeido(idNotificacion);

        return { previous };
      },
      onError: (error) => {
        console.error('onError', error);
      },
    },
  );

  const value = useMemo(() => {
    const cantidadNoLeidas =
      notificaciones?.filter((n) => !n?.leida).length || 0;
    const cantidadNoLeidasTuArsat =
      notificaciones?.filter(
        (n) => !n?.leida && n?.tipo === NOTIFICACIONES.TU_ARSAT,
      ).length || 0;

    const cantidadNoLeidasTuEquipo =
      notificaciones?.filter(
        (n) => !n?.leida && n?.tipo === NOTIFICACIONES.TU_EQUIPO,
      ).length || 0;

    const hasNotificacionesTuArsat = notificaciones?.some(
      (n) => n?.tipo === NOTIFICACIONES.TU_ARSAT,
    );
    const hasNotificacionesTuEquipo = notificaciones?.some(
      (n) => n?.tipo === NOTIFICACIONES.TU_EQUIPO,
    );

    return {
      notificaciones,
      error,
      isConnected,
      cantidadNoLeidas,
      cantidadNoLeidasTuArsat,
      cantidadNoLeidasTuEquipo,
      hasNotificacionesTuArsat,
      hasNotificacionesTuEquipo,
      hasResolvedInitialNotifications,
      marcarComoLeida,
    };
  }, [notificaciones, error, isConnected, hasResolvedInitialNotifications]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);

  if (!context) {
    throw new Error(
      'useNotificationsContext debe usarse dentro de NotificationsProvider',
    );
  }

  return context;
};
